import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from '../../../context/LanguageContext';
import ModalHeader from './ModalHeader';
import ModalFooter from './ModalFooter';
import './CheckoutModal.css';
import { Form, Radio, Input, message } from 'antd';

const CheckoutModal = ({ onClose, onConfirm }) => {
  const { t, language } = useTranslation();
  const [form] = Form.useForm();
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [customerType, setCustomerType] = useState('customer');
  const [companyName, setCompanyName] = useState('');
  const [companyTaxNumber, setCompanyTaxNumber] = useState('');
  const [taxNumberError, setTaxNumberError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formValid, setFormValid] = useState(true);
  const [validationTriggered, setValidationTriggered] = useState(false);

  const validateForm = useCallback(() => {
    if (customerType === 'company') {
      // Check if company name is filled
      const nameValid = companyName.trim() !== '';
      
      // Validate tax number format (must be 15 digits)
      const taxNumberValid = /^\d{15}$/.test(companyTaxNumber);
      
      // Set appropriate error message for tax number
      if (!companyTaxNumber) {
        setTaxNumberError(t('cashier.payment.enterTaxNumber'));
      } else if (!taxNumberValid) {
        setTaxNumberError(t('cashier.payment.invalidTaxNumber'));
      } else {
        setTaxNumberError('');
      }
      
      const isValid = nameValid && taxNumberValid;
      setFormValid(isValid);
      return isValid;
    }
    return true;
  }, [customerType, companyName, companyTaxNumber, t]);

  useEffect(() => {
    // Only validate form when values change if validation has already been triggered
    if (validationTriggered && customerType === 'company') {
      validateForm();
    } else if (customerType !== 'company') {
      setFormValid(true); // Always valid for regular customers
    }
  }, [customerType, validationTriggered, validateForm]);

  // validateForm is now defined above using useCallback

  const handleConfirm = () => {
    // Trigger validation when trying to submit
    setValidationTriggered(true);
    
    if (customerType === 'company' && !validateForm()) {
      message.error(t('cashier.payment.messages.error'));
      return;
    }

    setLoading(true);
    const orderData = {
      payment_method: paymentMethod,
      customer_type: customerType
    };
    
    if (customerType === 'company') {
      orderData.company_name = companyName.trim();
      orderData.company_tax_number = companyTaxNumber.trim();
    }
    
    onConfirm(orderData);
  };

  const isRTL = language === 'ar';

  return ReactDOM.createPortal(
    <div className={`modal-backdrop${isRTL ? ' rtl' : ''}`}>
      <div className="checkout-modal">
        <ModalHeader title={t('cashier.payment.select')} onClose={onClose} />

        <div className="checkout-modal-content">
          {/* Payment Method Selection */}
          <h3 className="modal-section-title">{t('cashier.payment.select')}</h3>
          <div className="payment-method-options">
            <div
              className={`payment-option ${paymentMethod === 'cash' ? 'selected' : ''}`}
              onClick={() => setPaymentMethod('cash')}
            >
              <div className="payment-icon">💵</div>
              <div className="payment-label">{t('cashier.payment.methods.cash')}</div>
            </div>

            <div
              className={`payment-option ${paymentMethod === 'card' ? 'selected' : ''}`}
              onClick={() => setPaymentMethod('card')}
            >
              <div className="payment-icon">💳</div>
              <div className="payment-label">{t('cashier.payment.methods.card')}</div>
            </div>
          </div>

          {/* Customer Type Selection */}
          <div className="customer-type-section">
            <h3 className="modal-section-title">{t('cashier.payment.customerType')}</h3>
            <Form form={form} layout="vertical" size="small" style={{ margin: 0 }}>
              <Form.Item>
                <Radio.Group
                  value={customerType}
                  onChange={(e) => {
                    setCustomerType(e.target.value);
                    setValidationTriggered(false); // Reset validation when changing customer type
                  }}
                  style={{ width: '100%' }}
                  buttonStyle="solid"
                >
                  <Radio.Button value="customer" style={{ width: '50%', textAlign: 'center' }}>
                    {t('cashier.payment.regularCustomer')}
                  </Radio.Button>
                  <Radio.Button value="company" style={{ width: '50%', textAlign: 'center' }}>
                    {t('cashier.payment.companyCustomer')}
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>

              {/* Company Information (conditional) */}
              {customerType === 'company' && (
                <div className="company-info">
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Form.Item
                      label={t('cashier.payment.companyName')}
                      required
                      validateStatus={!companyName && !formValid && validationTriggered ? 'error' : ''}
                      help={!companyName && !formValid && validationTriggered ? '' : ''}
                      style={{ marginBottom: 4, flex: 1 }}
                    >
                      <Input
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder={t('cashier.payment.enterCompanyName')}
                        status={!companyName && !formValid && validationTriggered ? 'error' : ''}
                        size="small"
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('cashier.payment.taxNumber')}
                      required
                      validateStatus={validationTriggered && taxNumberError ? 'error' : ''}
                      help={validationTriggered && taxNumberError ? taxNumberError : ''}
                      style={{ marginBottom: 4, flex: 1 }}
                    >
                      <Input
                        value={companyTaxNumber}
                        onChange={(e) => {
                          // Only allow numeric input
                          const value = e.target.value.replace(/\D/g, '');
                          setCompanyTaxNumber(value);
                          if (validationTriggered) validateForm();
                        }}
                        placeholder={t('cashier.payment.enterTaxNumber')}
                        status={validationTriggered && taxNumberError ? 'error' : ''}
                        size="small"
                        maxLength={15}
                      />
                    </Form.Item>
                  </div>
                  {validationTriggered && !formValid && !taxNumberError && (!companyName || !companyTaxNumber) && (
                    <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '2px' }}>
                      {!companyName && !companyTaxNumber ? t('cashier.payment.enterCompanyName') + ' & ' + t('cashier.payment.enterTaxNumber') :
                       !companyName ? t('cashier.payment.enterCompanyName') : t('cashier.payment.enterTaxNumber')}
                    </div>
                  )}
                </div>
              )}
            </Form>
          </div>
        </div>

        <ModalFooter
          onCancel={onClose}
          onConfirm={handleConfirm}
          loading={loading}
          disabled={customerType === 'company' && !formValid}
        />
      </div>
    </div>,
    document.body
  );
};

export default CheckoutModal;
